/**
 * GENERATED FILE — DO NOT HAND-EDIT.
 *
 * Produced by scripts/generate.mjs from the canonical Markdown sources
 * committed at the package root: terms-and-conditions.md, privacy-policy.md.
 * To change the legal text, edit those .md files first, then re-run
 * `pnpm --filter @repo/legal run generate` (or `run build`, which runs it
 * automatically) and commit the result.
 */

export const TERMS_AND_CONDITIONS_MARKDOWN = `# Terms of service

Effective from October, 2026

These Terms of Service (“Terms”) govern access to and use of Vera’s platform, products, and services (the “Service”) by the customer (“Customer”). By accessing or using the Service, Customer agrees to be bound by these Terms.

#### 1. Overview of Services

Vera provides a platform that connects physical products to digital identities through tags, certificates, and related infrastructure. The Service enables authentication, ownership tracking, and lifecycle data associated with products.

#### 2. Eligibility and Authority

Customer represents and warrants that it has the legal authority to enter into this Agreement. If Customer is accessing the Service on behalf of an organization, Customer agrees to bind that organization to these Terms.

#### 3. Use of the Service

Customer agrees to use the Service only for lawful purposes and in accordance with these Terms. Customer shall not misuse the Service, interfere with its operation, or attempt to access it in an unauthorized manner.

Customer shall not use the Service to engage in fraudulent activity, misrepresent product authenticity or ownership, or violate applicable laws or third-party rights.

#### 4. Customer Accounts

Customer is responsible for maintaining the confidentiality of its account credentials and for all activities conducted under its account. Customer agrees to notify Vera promptly of any unauthorized use or security breach.

#### 5. Products, Tags, and Certificates

The Service includes physical tags and digital certificates that link products to identity and data.

Tags are uniquely encoded and must not be altered, duplicated, or tampered with. Certificates represent digital records of product information, including authenticity, ownership, and provenance.

Customer is solely responsible for the accuracy and legitimacy of all product data associated with the Service.

#### 6. Customer Data and End-User Data

Customer may submit or store product data and end-user data through the Service (“Customer Data”).

Customer retains ownership of its Customer Data. Customer is responsible for ensuring that it has all necessary rights and permissions to collect, use, and share such data with Vera.

Vera may process Customer Data solely for the purpose of providing, maintaining, and improving the Service.

Customer acknowledges that end users may submit or interact with data through the Service, and Customer is responsible for compliance with all applicable data protection and privacy laws related to such data.

#### 7. Data Rights and Usage

Vera may use aggregated and anonymized data derived from the use of the Service to operate, analyze, and improve its platform.

Vera does not sell Customer Data to third parties.

#### 8. SMS / Text Messaging Terms

By providing your mobile phone number and enabling SMS notifications in the Vera app, you consent to receive automated text messages from Vera at that number. Consent is not a condition of purchase or of using Vera.

Message types: one-time verification codes, notifications about your certificates (scans and ownership transfers), and, if separately enabled, product news and offers.

Message frequency varies based on your account activity and the categories you enable. Message and data rates may apply.

Reply STOP to any message to opt out, or HELP for assistance. You can also turn off. SMS notifications at any time in the app on the Notifications page.

Vera does not sell, rent, or share mobile phone numbers or SMS opt-in data with third parties or affiliates for marketing purposes.

#### 9. Intellectual Property

Vera retains all rights, title, and interest in and to the Service, including all software, technology, and intellectual property.

Except for the limited rights expressly granted under these Terms, no rights are granted to Customer.

**Customer name and logo.** If Customer uses the Service as a business or other organization (for example, a brand that issues tags or certificates through the Service), Customer grants Vera a non-exclusive, worldwide, royalty-free right to use Customer’s name and logo to identify Customer as a customer of Vera on Vera’s websites and in Vera’s marketing materials, consistent with any reasonable trademark usage guidelines Customer provides to Vera. Vera will not state or imply that Customer endorses Vera or any Vera product, beyond the existence of the customer relationship, without Customer’s prior written consent. All goodwill arising from Vera’s use of Customer’s name and logo inures to Customer.

Customer may withdraw this permission at any time by written notice to [legal@vera.inc](mailto:legal@vera.inc). After receiving notice, Vera will stop making new uses of Customer’s name and logo within a reasonable time, and is not required to recall or alter materials already printed or distributed before the notice. This paragraph and the one before it do not apply to individuals who use the Service to view, hold, or transfer certificates for their own products.

#### 10. Fees and Payment

Certain features of the Service may require payment. Customer agrees to pay all applicable fees as described at the time of purchase.

All payments are non-refundable unless otherwise stated. Vera reserves the right to modify pricing upon notice.

#### 11. Third-Party Applications

The Service may integrate with or interact with third-party products, services, or applications that are not owned or controlled by Vera (each, a “Third-Party Application”). Customer may choose, at its sole discretion, to use such Third-Party Applications.

If required for interoperability, Customer may provide Vera with access credentials solely to enable integration. Customer represents that it has the right to provide such credentials.

Vera does not endorse or assume responsibility for Third-Party Applications. Customer’s use of such applications is governed by separate agreements with their providers and is at Customer’s own risk.

#### 12. Disclaimers

The Service is provided on an “as is” and “as available” basis. Vera makes no warranties regarding the reliability, availability, or accuracy of the Service.

Vera does not guarantee the prevention of fraud, misuse, or unauthorized activity related to products or certificates.

#### 13. Limitation of Liability

To the maximum extent permitted by law, Vera shall not be liable for any indirect, incidental, special, or consequential damages.

Vera’s total liability under these Terms shall not exceed the amount paid by Customer to Vera in the twelve (12) months preceding the claim.

#### 14. Termination

Vera may suspend or terminate Customer’s access to the Service if Customer violates these Terms.

Customer may discontinue use of the Service at any time.

#### 15. Modifications to Terms

Vera may update these Terms from time to time. Continued use of the Service after such updates constitutes acceptance of the revised Terms.

#### 16. Governing Law

These Terms shall be governed by and construed in accordance with the laws of [Insert Jurisdiction], without regard to conflict of law principles.

#### 17. Contact

For questions regarding these Terms, please contact: [legal@vera.inc](mailto:legal@vera.inc)
`

export const PRIVACY_POLICY_MARKDOWN = `# Privacy policy

Effective from January 2026. Last updated 1 September 2026.

This Privacy Policy explains how Vera Inc. (“Vera”, “we”) collects, uses and shares
personal data.

#### 1. What this policy covers

Vera connects physical products to digital certificates. A brand attaches a Vera tag
to a product; tapping or scanning that tag opens a certificate showing what the
product is, who owns it and its history.

This policy covers four things:

- **The Vera console** at \`platform.vera.inc\`, used by brand staff.
- **The Vera app** for iOS and Android, used by brand staff to add certificates to
  products.
- **Certificate pages**, which are what someone sees after tapping or scanning a tag.
  These are presented under the brand's own name.
- **Our website** at \`vera.inc\`.

#### 2. Our role: when we are a controller, and when we are a processor

This distinction decides who you should contact about your data, so it is worth
stating plainly.

**Vera is a processor** for the data a brand collects through its certificates. The
brand decides what is collected, why, and on what legal basis. Scan records, the email
and location captured by a certificate's access gate, and the product data shown on a
certificate all belong to that brand. If you scanned a tag and want your data removed,
contact the brand you bought the product from. We will help them respond.

**Vera is a controller** for: brand staff accounts and their use of the console and the
app; support requests and error reports sent to us; consumer accounts held directly
with Vera; billing; and our own website.

#### 3. Information we collect

**Brand staff (console and app).** Name, email address, phone number where provided,
profile photo, the company and role you belong to, and your interface preferences. We
record sign-in events. Authentication is handled by WorkOS, including sign-in with
Google, Microsoft or Apple and email magic links, so your password (where you use one)
is held by WorkOS rather than by us.

We also record limited first-party product usage: which steps of an in-product tour
were shown, and automatic error reports when a page fails to render. Neither carries
free text about you. See section 6.

**People who tap or scan a tag.** Each scan is recorded against the certificate. That
record includes the time, the tag and certificate identifiers, the result of the
authenticity check, your IP address, your browser's user-agent string, the referring
page, and a device fingerprint stored as a hash.

A brand may additionally turn on an access gate before the certificate is shown. Where
it is on, we collect the **email address** you enter. Location is separate and is
covered in section 4. Both of these settings are the brand's choice, not ours, and
either may be off.

If you have opted into the access gate, your email address and your choice are stored
in your browser's local storage for up to 180 days so you are not asked again on the
same device.

**Consumers with a Vera account.** Registering an account or claiming ownership of a
product is optional. If you do, we hold your email address, name, and any of the
following you choose to provide: phone number, profile photo, username, date of birth,
gender, country, and notification preferences. Ownership transfers are addressed by the
recipient's **email address**.

**Billing.** Card payments are taken by Stripe using Stripe's own hosted card fields.
**Card numbers never reach Vera's systems.** We store only Stripe's identifiers for a
customer, subscription and payment method.

**Brand-supplied data.** Where a brand connects an external store such as Shopify, we
import the customer and order records it sends us, which may include names, email
addresses, phone numbers and shipping addresses. That import is the brand's decision.

#### 4. Location

Location is the area where a policy is most often wrong in one direction or the other,
so this section is specific.

**We do not collect location in the Vera app.** The iOS and Android app contains no
location capability at all.

**We do not derive your location from your IP address** to populate a certificate's
scan record.

**On certificate pages, we collect precise location only where two conditions are both
met**: the brand has enabled location collection, and you have ticked the consent box
presented to you. If either is missing, no location is requested and none is stored.
Where both are met, your browser supplies latitude and longitude (requested at reduced
accuracy) which we store with the scan record.

To make those coordinates readable, we send them to the **Google Maps Geocoding API**,
which returns a city, state and country. Your coordinates therefore leave our systems
and reach Google for this purpose.

#### 5. The camera and NFC in the Vera app

The Vera app is a tool for brand staff, and its permissions are narrow.

**NFC.** The app reads the web address encoded on a Vera tag when you hold the phone
against it, and sends that address to us so the tag can be added to the scanning
session you are working in. That address contains the tag's identifier and a
cryptographic proof. The app deliberately does not read or transmit the chip's raw
hardware identifier.

**Camera.** The camera is used for one thing: reading the QR code that starts a
scanning session. The code is decoded on the device and only the decoded text is used.
**No photograph or video frame is captured, stored or transmitted.**

**What the app does not do.** It has no location capability, collects no device or
advertising identifier, and contains no analytics, attribution, advertising or
crash-reporting software. It does not track you across other companies' apps or
websites. The only thing it stores on the device is your light or dark theme
preference.

As with any request over the internet, our servers observe the phone's **IP address**.
We record it in our server logs so requests can be correlated when something goes
wrong.

#### 6. Support requests and error reports

When you contact support from the console or the app, we receive what you write, any
files you attach, and technical context that makes the report actionable: the page you
were on, your browser or device platform and operating system version, time zone, and the
version of the software you were running. From the console this also includes the size of
your browser window; from the app, the scanning session you were in, where there is one.

The console also files an error report automatically when a page fails to render. That
report contains the error and the technical context above. It does not contain anything
you have typed.

Reports sent from the Vera app are not tied to a user account, because the app has no
sign-in. This means we generally cannot reply to them directly. If you need an answer,
email us as well.

#### 7. SMS / text messaging

Vera sends text messages only to users who opt in within the Vera app. We do not sell,
rent, or share mobile phone numbers or SMS opt-in data with third parties or affiliates
for marketing purposes.

Message frequency varies based on your account activity and the notification categories
you enable. Message and data rates may apply. Reply STOP to opt out at any time, or
HELP for assistance.

#### 8. How we use information

We use personal data to operate and secure the services; to verify that a product is
genuine and to record its ownership and history; to show a brand how its certificates
are being used; to answer support requests and fix defects; to take payment; to send
service messages, and marketing messages where you have agreed to them; and to meet our
legal obligations.

Where the GDPR or UK GDPR applies, we rely on: performance of a contract (running an
account); consent (location on certificate pages, SMS, marketing); our legitimate
interests in securing the services, preventing counterfeiting and improving the
product; and compliance with law.

#### 9. AI features

The Vera console includes an assistant that answers questions about a brand's own data.
When a brand's staff member asks a question, that question and the results of the
database queries run to answer it are sent to **Google Vertex AI** for processing.

Those results can include personal data belonging to that brand's customers, such as a
customer's name, email address or city, where the question asked for it and the staff
member has permission to see it. Queries are restricted to the asking user's own
company.

We store the question that was asked. We do not store the assistant's answer or the
query results.

We do **not** use identifiable customer data to train AI models. We may use aggregated
or de-identified data to improve the service.

#### 10. Sharing and subprocessors

We do not sell personal data.

We share personal data with service providers who help us run the service:

- **Google Cloud Platform** — hosting, database, cache, file storage and secrets
- **Google Vertex AI** — the assistant described in section 9
- **Google Maps Platform** — turning coordinates into a place name
- **Google Fonts** — serving fonts on certificate pages, which discloses your IP
  address to Google
- **WorkOS** — sign-in and identity
- **Stripe** — payments
- **SendGrid** — transactional email
- **Twilio** — text messages
- **Shopify** — where a brand connects its store

These providers are contractually bound to process data only as we instruct. A current
list is maintained with our [DPA](https://vera.inc/dpa).

We may also disclose data where the law requires it, to enforce our terms, or as part
of a merger or acquisition.

#### 11. International transfers

We are based in the United States and our providers operate internationally, so your
data may be processed outside your country. Where required, we use appropriate
safeguards, including Standard Contractual Clauses.

#### 12. Retention

We keep personal data for as long as the account or certificate it belongs to exists,
and afterwards where we need it to meet a legal, accounting or security obligation, or
to resolve a dispute.

Scan records are treated differently, and deliberately: a certificate's value is its
history, so deleting the record of a scan would delete part of the provenance the
product's owner relies on. We therefore retain scan records for as long as we provide the
service for that certificate, and **we do not delete them on a fixed schedule**.

You can ask us to delete data about you at any time. See section 14.

#### 13. Security

We protect personal data with access controls, encryption in transit, network isolation
for our databases, managed secret storage, and permission checks enforced on the server
rather than in the interface. No system is perfectly secure, and we do not claim
otherwise.

#### 14. Your rights

Depending on where you live, you may have the right to access the personal data we hold
about you, correct it, delete it, restrict or object to how we use it, receive a copy in
a portable form, and withdraw consent you have given. You may also have the right not to
be discriminated against for exercising these rights.

**Where to send a request.** If it concerns data collected through a brand's
certificates, contact that brand, because they decide what happens to it. If they pass
the request to us, or if it concerns your Vera account, a support request, or our
website, write to [legal@vera.inc](mailto:legal@vera.inc).

You can withdraw location consent by clearing your browser's site data, and stop text
messages by replying STOP.

#### 15. Children

The services are for businesses and adult consumers. They are not directed at children,
and we do not knowingly collect personal data from a child under 16. If you believe a
child has given us personal data, write to [legal@vera.inc](mailto:legal@vera.inc) and
we will delete it.

#### 16. Cookies and similar technologies

The Vera console uses cookies to keep you signed in. Certificate pages use your
browser's local storage to remember an access-gate choice, as described in section 3.

The console, the certificate pages and the Vera app contain **no third-party analytics,
advertising or crash-reporting software**. Our marketing website at \`vera.inc\` is a
separate, third-party-hosted site and may use cookies and analytics, which are described
where they are used.

#### 17. Changes to this policy

We may update this policy. Where a change is significant, we will take reasonable steps
to tell you. The date at the top records when it was last changed.

#### 18. Contact

Questions about this policy, or a request under section 14:
[legal@vera.inc](mailto:legal@vera.inc)

For help using Vera or the Vera app, see [our support page](https://platform.vera.inc/help).
`
